/* eslint-disable max-len */
import type { ChangeHandler, IconName } from 'forms';
import { Button } from 'forms';
import noop from 'lodash/noop';
import type { HTMLAttributes } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';
import useReCaptcha2Behavior from '../hooks/useReCaptcha2Behavior.client';
import { ReCaptchaText } from '@rcm/recaptcha-2';
import Box from '@mui/material/Box';
import '../styles.css';

interface Props extends HTMLAttributes<HTMLButtonElement> {
  action: string;
  badge?: 'bottomleft' | 'bottomright' | 'inline';
  btn?: string;
  challengeTabIndex?: number;
  className?: string;
  disabled?: boolean;
  icon?: IconName;
  iconr?: IconName;
  id?: string;
  name?: string;
  onError?: () => void;
  onExpired?: () => void;
  onValueChange?: ChangeHandler<string>;
  text?: string;
  title?: string;
  type?: 'submit' | 'button' | 'reset';
  value?: string;
  disableNotice?: boolean;
}

/**
 * Renders a button that will trigger ReCAPTCHA V2 invisible authentication.
 *
 * > **NOTE:** This requires that the sitekey configured for ReCAPTCHA V2 is for
 * > invisible captcha.
 *
 * When the button is clicked, a captcha verification will be performed against
 * the Google ReCAPTCHA API. If it passes, `onValueChanged()` will be called
 * with the captcha response.
 *
 * ## Automatic Disable
 *
 * The button will be disabled while captcha authentication is in progress, even
 * if the `disabled` property is absent or set to `false`. This effect ceases as
 * soon as captcha authentication returns regardless of whether authentication
 * passed or failed.
 *
 * ## Form Submission
 *
 * This component tries to retain the expected form submission behavior of
 * submit buttons.
 *
 * Presumably, the intention of a button with a `type` attribute of `"submit"`
 * is to submit the form containing it, which is the typical way for a React
 * form (and most other forms) to be submitted. However, Google ReCAPTCHA event
 * handlers normally block this behavior by default, which means clicking a
 * button that is tied to invisible ReCAPTCHA stops the form from being
 * submitted as expected. Therefore, if the `type` property of an
 * `InvisibleReCaptcha2Button` is set to `"submit"`, the component will dispatch
 * a `'submit'` event on the containing form element after `onValueChange()` is
 * called following a successful captcha authentication.
 *
 * > **NOTE:** The "containing form" is determined using
 * > [`Element.closest()`](https://developer.mozilla.org/en-US/docs/Web/API/Element/closest)
 * > on the rendered button element.
 *
 * ## Behavior When reCAPTCHA Is Disabled
 *
 * When reCAPTCHA is disabled in the current context, the button behaves more
 * like a normal button. This includes the following changes to behavior:
 *
 * - All behavioral differences for when reCAPTCHA is disabled are inherited
 *   from `useReCaptcha2Behavior()`. Additionally,
 * - Clicking the button does not trigger a reCAPTCHA verification, but instead
 *   immediately calls `onValueChange(null)`.
 *
 * @todo Will probably break if the component ever gets re-mounted after
 * unmounting
 * @todo Needs testing: Multiple rendered instances
 */
const InvisibleReCaptcha2Button = ({
  action,
  badge,
  btn,
  challengeTabIndex,
  className,
  disabled,
  icon,
  iconr,
  id,
  name,
  onError,
  onExpired,
  onValueChange = noop,
  text,
  title,
  type,
  disableNotice = false,
  ...props
}: Props) => {
  const buttonInnerRef = useRef<HTMLButtonElement>(null);

  const handleValueChange = useCallback<ChangeHandler<string>>(
    (...args) => {
      onValueChange(...args);
      if (type === 'submit') {
        (
          buttonInnerRef.current?.closest('form') as HTMLFormElement
        )?.dispatchEvent(
          new SubmitEvent('submit', {
            bubbles: true,
            cancelable: true,
            submitter: buttonInnerRef.current,
          })
        );
      }
    },
    [onValueChange, type]
  );

  const { beginCaptcha, attrs, isLoading } = useReCaptcha2Behavior({
    action,
    badge,
    challengeTabIndex,
    className,
    name,
    onError,
    onExpired,
    onValueChange: handleValueChange,
  });

  const isDisabled = useMemo(
    () => disabled || isLoading,
    [disabled, isLoading]
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      }}
    >
      {disableNotice ? null : <ReCaptchaText />}
      <Button
        {...props}
        {...attrs}
        btn={btn}
        disabled={isDisabled}
        icon={icon}
        iconr={iconr}
        id={id}
        innerRef={buttonInnerRef}
        onClick={beginCaptcha}
        text={text}
        title={title}
        type={type}
      />
    </Box>
  );
};

export default InvisibleReCaptcha2Button;
